// apps/admin/src/pages/PendingEntityMappingsPage.tsx
//
// Route 1 of the email-ingestion architectural fix (PR 4/4).
//
// Surfaces every auto-learned `entity_mappings` doc for a single company
// that needs founder review. Linked from PR #782's Google Chat alert action
// button (`email_ingestion_entity_auto_learned`) — when the founder clicks
// "View in Admin", they land here.
//
// Reuses the `confirmEntityMapping` callable (extended by PR #784 to handle
// both `pending_confirmation` and `pending_office_assignment` statuses).
// The lower-level EntityMappingManager component is embedded in the
// CompanyDetailsPage > Overview tab; this dedicated page is the
// alert-deep-link surface and only shows PENDING entries.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  doc,
  getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import {
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Building2,
  Mail,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface PendingMapping {
  entityCode: string;
  status: 'pending_confirmation' | 'pending_office_assignment';
  templateId?: string;
  templateName?: string;
  officeId?: string | null;
  officeName?: string | null;
  votedOfficeId?: string | null;
  votedOfficeName?: string | null;
  voteConfidence?: number | null;
  autoCreated?: boolean;
  entityPatterns?: string[];
  filenamePattern?: string;
  learnedFrom?: {
    messageId?: string;
    filename?: string;
    sentAt?: any; // Firestore Timestamp
    extractedAggregator?: string;
    rejectionReason?: string;
    voteResult?: {
      totalSibling: number;
      dominantOfficeId: string | null;
      percentage: number;
    } | null;
  };
}

interface Office {
  id: string;
  name: string;
}

// Brisbane time formatter (Australia/Brisbane = UTC+10, no DST).
// Mirrors the convention codified in CLAUDE.md + user memory.
function formatBrisbane(ts: any): string {
  if (!ts) return '—';
  const date = ts?.toDate
    ? ts.toDate()
    : ts?.seconds
    ? new Date(ts.seconds * 1000)
    : new Date(ts);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

const PendingEntityMappingsPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [companyName, setCompanyName] = useState<string>('');
  const [pending, setPending] = useState<PendingMapping[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  // Track the inline office picker for `pending_office_assignment` rows
  // before confirm. Keyed by entityCode.
  const [pickedOffice, setPickedOffice] = useState<Record<string, string>>({});
  // Track the inline override picker for `pending_confirmation` rows
  // (founder can disagree with the auto-voted office). Keyed by entityCode.
  const [overrideOffice, setOverrideOffice] = useState<Record<string, string>>(
    {},
  );

  const loadData = useCallback(
    async (initial: boolean) => {
      if (!companyId) return;
      if (initial) setLoading(true);
      else setRefreshing(true);
      try {
        // 1. Company name (for the header).
        const companySnap = await getDoc(doc(db, `companies/${companyId}`));
        if (companySnap.exists()) {
          setCompanyName(
            companySnap.data().companyName ||
              companySnap.data().name ||
              companyId,
          );
        }

        // 2. Offices for the picker dropdowns.
        const officesSnap = await getDocs(
          query(collection(db, `companies/${companyId}/offices`)),
        );
        const officeList: Office[] = officesSnap.docs.map((d) => ({
          id: d.id,
          name: d.data().name || d.id,
        }));
        setOffices(officeList);

        // 3. Pending entity_mappings.
        // We fetch ALL mappings via getEntityMappings (founder has tenant
        // access) then filter client-side for pending statuses. Volume is
        // <50 per company, so the round-trip cost is negligible compared
        // to wiring a new dedicated callable.
        const getEntityMappings = httpsCallable(functions, 'getEntityMappings');
        const result = (await getEntityMappings({ companyId })) as any;
        const all: PendingMapping[] = result?.data?.mappings ?? [];
        const filtered = all
          .filter(
            (m) =>
              m.status === 'pending_confirmation' ||
              m.status === 'pending_office_assignment',
          )
          .sort((a, b) => {
            // Newest `learnedFrom.sentAt` first.
            const aTs =
              a.learnedFrom?.sentAt?.seconds ??
              (a.learnedFrom?.sentAt?.toDate
                ? Math.floor(a.learnedFrom.sentAt.toDate().getTime() / 1000)
                : 0);
            const bTs =
              b.learnedFrom?.sentAt?.seconds ??
              (b.learnedFrom?.sentAt?.toDate
                ? Math.floor(b.learnedFrom.sentAt.toDate().getTime() / 1000)
                : 0);
            return bTs - aTs;
          });
        setPending(filtered);
      } catch (err) {
        console.error(
          '[PendingEntityMappingsPage] Failed to load data:',
          err,
        );
        toast.error('Failed to load pending entity mappings');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    void loadData(true);
  }, [loadData]);

  const handleConfirm = async (mapping: PendingMapping) => {
    if (!companyId) return;
    if (!mapping.templateId) {
      toast.error('Mapping is missing a templateId — cannot confirm');
      return;
    }

    // Resolve which officeId to send:
    //  - pending_office_assignment: must come from the picker.
    //  - pending_confirmation: use the override if picked, otherwise the
    //    voted office.
    let resolvedOfficeId: string | undefined;
    if (mapping.status === 'pending_office_assignment') {
      resolvedOfficeId = pickedOffice[mapping.entityCode];
      if (!resolvedOfficeId) {
        toast.error('Pick an office before confirming');
        return;
      }
    } else {
      resolvedOfficeId =
        overrideOffice[mapping.entityCode] ||
        mapping.votedOfficeId ||
        mapping.officeId ||
        undefined;
      if (!resolvedOfficeId) {
        toast.error('No office to confirm — pick one to override');
        return;
      }
    }

    setActionInFlight(mapping.entityCode);
    try {
      const confirmEntityMapping = httpsCallable(
        functions,
        'confirmEntityMapping',
      );
      await confirmEntityMapping({
        companyId,
        entityCode: mapping.entityCode,
        templateId: mapping.templateId,
        officeId: resolvedOfficeId,
      });
      toast.success(`Confirmed ${mapping.entityCode}`);
      await loadData(false);
    } catch (err) {
      console.error(
        '[PendingEntityMappingsPage] confirmEntityMapping failed:',
        err,
      );
      toast.error(`Failed to confirm ${mapping.entityCode}`);
    } finally {
      setActionInFlight(null);
    }
  };

  const handleReject = async (mapping: PendingMapping) => {
    if (!companyId) return;
    if (
      !window.confirm(
        `Reject auto-learned mapping for ${mapping.entityCode}?\n\n` +
          `This deletes the mapping. The pipeline will re-learn it if the ` +
          `aggregator emails another file with this entity code.`,
      )
    ) {
      return;
    }
    setActionInFlight(mapping.entityCode);
    try {
      const deleteEntityMapping = httpsCallable(
        functions,
        'deleteEntityMapping',
      );
      await deleteEntityMapping({
        companyId,
        entityCode: mapping.entityCode,
      });
      toast.success(`Rejected ${mapping.entityCode}`);
      await loadData(false);
    } catch (err) {
      console.error(
        '[PendingEntityMappingsPage] deleteEntityMapping failed:',
        err,
      );
      toast.error(`Failed to reject ${mapping.entityCode}`);
    } finally {
      setActionInFlight(null);
    }
  };

  if (!companyId) {
    return (
      <div className="text-slate-400">No companyId in route — invalid URL.</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to={`/companies/${companyId}`}
            className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200 mb-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to {companyName || 'company'}
          </Link>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-amber-400" />
            Pending Entity Mappings
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Auto-learned entity codes from email ingestion that need founder
            review.
            {companyName && (
              <span className="ml-1 text-slate-300">{companyName}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => void loadData(false)}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 text-slate-200 rounded-md hover:bg-slate-700 disabled:opacity-50 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-slate-400">Loading pending entity mappings…</div>
      ) : pending.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-white mb-1">
            All entity mappings up to date
          </h3>
          <p className="text-slate-400 text-sm">
            No pending auto-learned entries for this company.
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-400 text-sm border-b border-slate-700 bg-slate-900/40">
                <th className="px-4 py-3">Entity Code</th>
                <th className="px-4 py-3">Template</th>
                <th className="px-4 py-3">Office</th>
                <th className="px-4 py-3">Learned From</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((mapping) => {
                const isPendingOffice =
                  mapping.status === 'pending_office_assignment';
                const isPendingConfirm =
                  mapping.status === 'pending_confirmation';
                const inFlight = actionInFlight === mapping.entityCode;

                const voteText = mapping.learnedFrom?.voteResult
                  ? `${mapping.votedOfficeName || mapping.learnedFrom.voteResult.dominantOfficeId || '—'} — ${Math.round(
                      mapping.learnedFrom.voteResult.percentage * 100,
                    )}% of ${mapping.learnedFrom.voteResult.totalSibling} sibling mapping(s)`
                  : 'No vote — needs office assignment';

                return (
                  <tr
                    key={mapping.entityCode}
                    className="border-b border-slate-700/50 hover:bg-slate-800/40"
                  >
                    <td className="px-4 py-3 font-mono text-indigo-300">
                      {mapping.entityCode}
                      {mapping.autoCreated && (
                        <span className="ml-2 px-1.5 py-0.5 bg-blue-600/20 text-blue-300 rounded text-[10px] uppercase tracking-wide">
                          auto-learned
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {mapping.templateName || mapping.templateId || (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {isPendingOffice ? (
                        <select
                          value={pickedOffice[mapping.entityCode] || ''}
                          onChange={(e) =>
                            setPickedOffice((prev) => ({
                              ...prev,
                              [mapping.entityCode]: e.target.value,
                            }))
                          }
                          className="px-2 py-1 bg-slate-800 border border-amber-600/50 rounded text-white text-sm"
                          disabled={inFlight}
                        >
                          <option value="">Pick office…</option>
                          {offices.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-3.5 h-3.5 text-slate-500" />
                            <span>
                              {mapping.votedOfficeName ||
                                mapping.officeName ||
                                '—'}
                            </span>
                          </div>
                          <select
                            value={overrideOffice[mapping.entityCode] || ''}
                            onChange={(e) =>
                              setOverrideOffice((prev) => ({
                                ...prev,
                                [mapping.entityCode]: e.target.value,
                              }))
                            }
                            className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-slate-300 text-xs"
                            disabled={inFlight}
                          >
                            <option value="">
                              Override office (optional)…
                            </option>
                            {offices.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">
                      <div className="flex items-start gap-2">
                        <Mail className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-slate-300">
                            {mapping.learnedFrom?.filename || '—'}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {formatBrisbane(mapping.learnedFrom?.sentAt)}
                          </div>
                          {isPendingConfirm && (
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              auto-voted: {voteText}
                            </div>
                          )}
                          {isPendingOffice && (
                            <div className="text-[11px] text-red-300/80 mt-0.5">
                              {voteText}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {isPendingConfirm && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600/20 text-amber-300 rounded text-xs">
                          <AlertCircle className="w-3 h-3" />
                          Pending review
                        </span>
                      )}
                      {isPendingOffice && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-600/20 text-red-300 rounded text-xs">
                          <AlertCircle className="w-3 h-3" />
                          Pick office
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => void handleConfirm(mapping)}
                        disabled={
                          inFlight ||
                          (isPendingOffice && !pickedOffice[mapping.entityCode])
                        }
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30 disabled:opacity-40 disabled:cursor-not-allowed rounded text-xs"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Confirm
                      </button>
                      <button
                        onClick={() => void handleReject(mapping)}
                        disabled={inFlight}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600/10 text-red-300 hover:bg-red-600/20 disabled:opacity-40 rounded text-xs"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PendingEntityMappingsPage;
